import {
  AlertCircle,
  AlertTriangle,
  CalendarClock,
  Droplets,
  Fuel,
  Gauge,
  Info,
  MapPinCheck,
  MapPinX,
  Navigation,
  OctagonMinus,
  Signpost,
  SquareParking,
  Thermometer,
  UserCheck,
  UserCog,
  UserX,
  type LucideIcon,
} from "lucide-react";
import type { Notification } from "@/types/fleet";

/**
 * Icon and colour for a notification `kind` — a vehicle event type, or `cost_<severity>` for a
 * reminder. Colours reuse what the app already draws: zone events in their zone colour
 * (`VehicleMap` `zoneColors`), `started`/`stopped` as the green start arrow and red stop octagon of
 * a History route. The event that opens a condition gets a filled chip; the one that closes it an
 * outlined chip in the same colour.
 *
 * Every lookup falls back to the severity, so a type upstream adds tomorrow still renders.
 */

export type NotificationTone = "primary" | "success" | "destructive" | "warning" | "driver";
export type NotificationEmphasis = "strong" | "soft";

export interface NotificationStyle {
  icon: LucideIcon;
  tone: NotificationTone;
  emphasis: NotificationEmphasis;
}

const NOTIFICATION_STYLES: Record<string, NotificationStyle> = {
  parking_enter: { icon: SquareParking, tone: "primary", emphasis: "strong" },
  parking_exit: { icon: SquareParking, tone: "primary", emphasis: "soft" },
  go_zone_enter: { icon: MapPinCheck, tone: "success", emphasis: "strong" },
  go_zone_exit: { icon: MapPinCheck, tone: "success", emphasis: "soft" },
  nogo_zone_enter: { icon: MapPinX, tone: "destructive", emphasis: "strong" },
  nogo_zone_exit: { icon: MapPinX, tone: "destructive", emphasis: "soft" },

  driver_identified: { icon: UserCheck, tone: "driver", emphasis: "strong" },
  driver_changed: { icon: UserCog, tone: "driver", emphasis: "strong" },
  driver_removed: { icon: UserX, tone: "driver", emphasis: "soft" },

  started: { icon: Navigation, tone: "success", emphasis: "strong" },
  stopped: { icon: OctagonMinus, tone: "destructive", emphasis: "strong" },
  overheat: { icon: Thermometer, tone: "destructive", emphasis: "strong" },

  speeding_start: { icon: Gauge, tone: "destructive", emphasis: "strong" },
  speeding_end: { icon: Gauge, tone: "success", emphasis: "soft" },
  street_speeding_start: { icon: Signpost, tone: "destructive", emphasis: "strong" },
  street_speeding_end: { icon: Signpost, tone: "success", emphasis: "soft" },

  // The same fuel icons a History route uses for its refuel / fuel-drain flags.
  refuel: { icon: Fuel, tone: "success", emphasis: "strong" },
  missing_fuel: { icon: Droplets, tone: "destructive", emphasis: "strong" },

  cost_overdue: { icon: CalendarClock, tone: "destructive", emphasis: "strong" },
  cost_due_soon: { icon: CalendarClock, tone: "warning", emphasis: "strong" },
  cost_upcoming: { icon: CalendarClock, tone: "primary", emphasis: "strong" },
};

const SEVERITY_FALLBACK: Record<Notification["type"], NotificationStyle> = {
  alert: { icon: AlertCircle, tone: "destructive", emphasis: "strong" },
  warning: { icon: AlertTriangle, tone: "warning", emphasis: "strong" },
  info: { icon: Info, tone: "primary", emphasis: "strong" },
};

/**
 * Full literal class strings, never assembled from the tone name — Tailwind v4 only generates
 * utilities it can find verbatim in the source.
 */
export const TONE_CLASSES: Record<
  NotificationTone,
  { strong: string; soft: string; accent: string; bar: string }
> = {
  primary: {
    strong: "bg-primary/15 text-primary",
    soft: "border border-primary/40 text-primary",
    accent: "border-l-primary",
    bar: "bg-primary",
  },
  success: {
    strong: "bg-success/15 text-success",
    soft: "border border-success/40 text-success",
    accent: "border-l-success",
    bar: "bg-success",
  },
  destructive: {
    strong: "bg-destructive/15 text-destructive",
    soft: "border border-destructive/40 text-destructive",
    accent: "border-l-destructive",
    bar: "bg-destructive",
  },
  warning: {
    strong: "bg-warning/15 text-warning",
    soft: "border border-warning/40 text-warning",
    accent: "border-l-warning",
    bar: "bg-warning",
  },
  driver: {
    strong: "bg-driver/15 text-driver",
    soft: "border border-driver/40 text-driver",
    accent: "border-l-driver",
    bar: "bg-driver",
  },
};

export function notificationStyle(
  kind: string | undefined,
  severity: Notification["type"],
): NotificationStyle {
  return (kind && NOTIFICATION_STYLES[kind]) || SEVERITY_FALLBACK[severity];
}

/** The coloured left edge for a toast or a list row. */
export function notificationAccentClass(
  kind: string | undefined,
  severity: Notification["type"],
): string {
  return TONE_CLASSES[notificationStyle(kind, severity).tone].accent;
}

/**
 * A tone as a CSS colour value, for Leaflet `divIcon` HTML: it sits in the document, so the theme
 * variables resolve there too and the map matches the toasts exactly. Each tone is named after its
 * `--<tone>` variable in `app/globals.css`.
 */
export function toneCss(tone: NotificationTone, alpha = 1): string {
  return alpha === 1 ? `hsl(var(--${tone}))` : `hsl(var(--${tone}) / ${alpha})`;
}

/** Sprite symbol id for a style — see `NotificationIconSprite`. */
export function notificationIconKey(kind: string | undefined, severity: Notification["type"]): string {
  return kind && NOTIFICATION_STYLES[kind] ? kind : `severity-${severity}`;
}

/** Every icon a notification can render, keyed like {@link notificationIconKey}. */
export const NOTIFICATION_ICON_ENTRIES: readonly (readonly [string, LucideIcon])[] = [
  ...Object.entries(NOTIFICATION_STYLES).map(([kind, style]) => [kind, style.icon] as const),
  ...Object.entries(SEVERITY_FALLBACK).map(([severity, style]) => [`severity-${severity}`, style.icon] as const),
];

export const NOTIFICATION_ICON_SYMBOL_PREFIX = "notification-icon-";

/**
 * Inline SVG for Leaflet HTML, drawn from the sprite so the map uses the exact lucide icons without
 * rendering React inside a map effect. `color` feeds lucide's `currentColor` stroke.
 */
export function notificationIconSvg(
  kind: string | undefined,
  severity: Notification["type"],
  size: number,
  color: string,
): string {
  const id = `${NOTIFICATION_ICON_SYMBOL_PREFIX}${notificationIconKey(kind, severity)}`;
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" aria-hidden="true" style="flex-shrink:0;color:${color};"><use href="#${id}"/></svg>`;
}
